/* eslint-disable global-require */
// Gate 1 trial entry (REACT_APP_TARGET=gate1). Load order is deliberate and
// uses require() so it is explicit:
//   1. the game's main module, imported but never rendered, so cold start
//      carries the production app's JavaScript weight
//   2. Firebase (app + auth from FirebaseConfig, Firestore with production
//      settings) → "firebaseReady"
//   3. anonymous sign-in → one document read → the war screen
const jsEntryPerf = window.__GATE1_JS_ENTRY ?? performance.now()
const gameMain = require('../main')
const gameBundleEvaluatedPerf = performance.now()
// Proof in every startup log that the game's main module (App, contexts,
// CSS) was evaluated, not just bundled.
const GAME_BUNDLE = { evaluated: typeof gameMain.renderGame === 'function', evalMs: Math.round((gameBundleEvaluatedPerf - jsEntryPerf) * 10) / 10 }

const { app: firebaseApp, auth } = require('../FirebaseConfig')
const { getGameFirestore, FIRESTORE_SETTINGS } = require('../FirestoreConfig')
const db = getGameFirestore(firebaseApp)
const firebaseReadyPerf = performance.now()

// gate1.css's page rules apply only under this class (the lab bundles the file too).
document.documentElement.classList.add('gate1')
require('./gate1.css')
const React = require('react')
const ReactDOM = require('react-dom/client')
const Gate1App = require('./Gate1App').default
const { BUILD, debugLog } = require('./buildInfo')
const { Gate1, isNative } = require('./nativeBridge')
const { createStartupMarks } = require('./core/startupMarks')
const { createFrameLogger } = require('./core/frameLogger')
const { logFileName } = require('./core/runLog')
const { createRunController, ARM_DELAY_MS } = require('./core/runController')
const { runStartup } = require('./startup')
const { bootCrashCounter, crashSnapshot } = require('./crashCounter')
const { collectDeviceInfo } = require('./deviceInfo')
const { buildRunLog, buildStartupLog, startupRecord } = require('./buildLog')
const { writeLog, writePng } = require('./logFiles')
const { FIXTURE_NAMES, SCENE_NAMES, DEFAULT_FIXTURE, REVEAL_FIXTURE, loadFixture } = require('./scene/fixtures')
const { createPlaceholderBeat } = require('./beats/placeholderBeat')
const { createRevealBeat, revealClashOf } = require('./beats/revealBeat')

const marks = createStartupMarks(performance.timeOrigin)
marks.mark('jsEntry', jsEntryPerf)
marks.mark('gameBundleEvaluated', gameBundleEvaluatedPerf)
marks.mark('firebaseReady', firebaseReadyPerf)

const logger = createFrameLogger()
let stage = null
let beat = null
let deviceInfo = { device: {}, native: {} }
let startupResult = null
let crashBoot = null
let firstRunPending = true

// Memory, sampled once a second; the peak since the last run was armed goes
// in each run log. Native: Android PSS of the app process (WebView GPU work
// runs in it), iOS phys_footprint of the app process. JS heap where the
// WebView exposes it (Chrome: performance.memory).
const mem = { peak: null, samples: 0 }
const resetMemPeak = () => { mem.peak = null; mem.samples = 0 }
setInterval(async () => {
    try {
        const m = await Gate1.memory()
        const js = performance.memory?.usedJSHeapSize
        const sample = { ...m, jsHeapKb: js != null ? Math.round(js / 1024) : null }
        mem.samples++
        if (!mem.peak) mem.peak = { ...sample }
        else for (const k of Object.keys(sample)) if (typeof sample[k] === 'number' && (mem.peak[k] == null || sample[k] > mem.peak[k])) mem.peak[k] = sample[k]
    } catch { /* not available */ }
}, 1000)
const isGraded = name => name === REVEAL_FIXTURE
let beatKind = 'reveal'
let beatGen = 0
let contextRebuild = Promise.resolve() // the scene rebuild after the last WebGL context restore
let jsErrors = 0
window.addEventListener('error', () => { jsErrors++ })
window.addEventListener('unhandledrejection', () => { jsErrors++ })

const controller = createRunController({ logger, build: BUILD, native: Gate1, onFinished: handleFinished })

// The war scene's fixture loads alongside the startup path; the stage draws
// it before its first frame.
const initialFixture = loadFixture(DEFAULT_FIXTURE).catch(e => { debugLog('fixture load failed', e); return null })

const deviceReady = collectDeviceInfo().then(d => { deviceInfo = d; return d })
const crashReady = bootCrashCounter()
    .then(r => { crashBoot = r.boot; return r })
    .catch(e => { crashBoot = { error: String(e) } })

const startupDone = (async () => {
    const info = await Gate1.getInfo().catch(() => ({}))
    if (info.processStartEpochMs != null) {
        marks.setEpoch('processStart', info.processStartEpochMs, info.processStartSource ?? 'native')
    }
    if (info.processStartRequestedEpochMs != null) {
        marks.setEpoch('processStartRequested', info.processStartRequestedEpochMs, 'native')
    }
    startupResult = await runStartup({ auth, db, mark: name => marks.mark(name, performance.now()) })
    debugLog('startup', startupResult)
    return startupResult
})()

function currentStartupRecord() {
    return { ...startupRecord({ marks: marks.marks, startup: startupResult, firestoreSettings: FIRESTORE_SETTINGS, crashBoot }), gameBundle: GAME_BUNDLE }
}

function onFirstWarFrame(perfMs) {
    marks.mark('firstWarFrame', perfMs)
    // One startup log per launch (cold-start trials never start a run).
    setTimeout(async () => {
        await Promise.all([deviceReady, crashReady])
        const crash = await crashSnapshot()
        const log = buildStartupLog({ build: BUILD, deviceInfo, crash, startup: currentStartupRecord() })
        await writeLog(logFileName({ kind: 'startup', epochMs: Date.now() }), log).catch(e => debugLog('startup log failed', e))
    }, 1000)
}

async function handleFinished(result) {
    result.beat = { name: beat.name, durationMs: beat.durationMs, stats: beat.stats?.() ?? null }
    result.drawCalls = stage.drawStats()
    result.memory = { peak: mem.peak, samples: mem.samples }
    const startup = firstRunPending ? currentStartupRecord() : null
    firstRunPending = false
    let summary
    try {
        await deviceReady
        const crash = await crashSnapshot()
        const log = buildRunLog({
            result, build: BUILD, deviceInfo, crash, stageInfo: stage.info(), startup, logger, jsErrors, armDelayMs: ARM_DELAY_MS,
        })
        const name = logFileName({ kind: 'run', label: result.cfg.label, epochMs: Date.now() })
        const w = await writeLog(name, log)
        const secs = log.run.durationMs != null ? (log.run.durationMs / 1000).toFixed(2) : '-'
        summary = `${result.cfg.label}: ${result.status}${result.abortReason ? ` (${result.abortReason})` : ''}, ${logger.count} frames in ${secs} s` +
            `${result.inject ? `, ${result.inject.type} injected ${result.inject.count}x` : ''}` +
            `${logger.overflow ? `, OVERFLOW ${logger.overflow}` : ''}\nsaved ${name} (${Math.round(w.bytes / 1024)} KB)`
    } catch (e) {
        summary = `${result.cfg.label}: log write FAILED: ${e.message ?? e}`
    }
    app.onRunWritten?.(summary)
}

Gate1.addListener('runAborted', () => controller.abort('paused'))

// Web only: ?autorun=<label>[&mode=sustained&minutes=N][&flash] starts one run
// after startup, for desktop verification. Never active in the native app.
function parseAutorun() {
    if (isNative) return null
    const q = new URLSearchParams(window.location.search)
    const label = q.get('autorun')
    if (!label) return null
    const sustained = q.get('mode') === 'sustained'
    return {
        label,
        mode: sustained ? 'sustained' : 'single',
        durationMs: sustained ? Number(q.get('minutes') || 10) * 60_000 : null,
        flash: q.has('flash'),
    }
}

const nextFrame = () => new Promise(r => requestAnimationFrame(() => r()))
const stampNow = () => new Date().toISOString().replace(/[-:]/g, '').replace(/\.\d+Z$/, 'Z')

const app = {
    build: BUILD,
    startupDone,
    initialFixture,
    fixtureNames: SCENE_NAMES,
    sceneName: () => stage?.scene?.name ?? null,
    beatKind: () => beatKind,
    async showFixture(name) {
        beat?.destroy()
        beat = null
        await stage.showScene(await loadFixture(name), { topUp: isGraded(name) })
        await app.rebuildBeat()
    },
    setBeatKind(kind) {
        beatKind = kind
        app.rebuildBeat()
    },
    // The beat follows the scene: the reveal beat when the scene's fixture
    // has a crater clash (and the reveal beat is chosen), else the placeholder.
    // The reveal beat builds its effect textures first (async); until it is
    // attached, ready() is false. A newer rebuild supersedes an older one.
    async rebuildBeat() {
        const gen = ++beatGen
        if (beat) beat.destroy()
        beat = null
        const useReveal = beatKind === 'reveal' && stage.scene && revealClashOf(stage.scene.fixture)
        const b = useReveal ? await createRevealBeat(stage).catch(e => { debugLog('reveal beat failed', e); return null }) : createPlaceholderBeat(stage)
        if (gen !== beatGen) { b?.destroy(); return }
        beat = b ?? createPlaceholderBeat(stage)
        controller.attach(stage, beat)
    },
    // Criterion #8 on device: each fixture drawn by real PixiJS, its display
    // tree checked against the rules output, a screenshot saved, one JSON log.
    async verifyFixtures() {
        const original = stage.scene?.name ?? null
        const stamp = stampNow()
        const results = []
        beat?.destroy()
        beat = null
        for (const name of FIXTURE_NAMES) {
            await stage.showScene(await loadFixture(name))
            await nextFrame(); await nextFrame() // rendered
            const v = stage.verifyScene()
            const shot = await writePng(`scene-${name}-${stamp}.png`, await stage.screenshot())
            results.push({
                fixture: name, pass: v.pass, expected: v.expected, rendered: v.rendered, counts: v.counts, screenshot: shot.name,
                problems: { missing: v.missing.slice(0, 20), extra: v.extra.slice(0, 20), wrongState: v.wrongState.slice(0, 20), wrongPos: v.wrongPos.slice(0, 20), hidden: v.hidden.slice(0, 20), dupes: v.dupes.slice(0, 20) },
            })
        }
        if (original) await stage.showScene(await loadFixture(original), { topUp: isGraded(original) })
        else stage.clearScene()
        await app.rebuildBeat()
        await deviceReady
        const passed = results.filter(r => r.pass).length
        const log = {
            schema: 'gate1.verify.v1', kind: 'verify', criterion: 8, writtenAtEpochMs: Date.now(),
            build: BUILD, device: deviceInfo.device, screen: { width: window.screen.width, height: window.screen.height, devicePixelRatio: window.devicePixelRatio },
            renderer: stage.info(), passed, of: results.length, results,
        }
        const name = `verify-${stamp}.json`
        await writeLog(name, log)
        return `Criterion #8: ${passed} of ${results.length} fixtures match the rules output\n` +
            results.map(r => `${r.fixture}: ${r.pass ? 'match' : 'MISMATCH'} (${r.rendered}/${r.expected} sprites)`).join('\n') + `\nsaved ${name}`
    },
    // Test only: lose the WebGL context (PixiJS restores it straight away),
    // wait for the scene rebuild, then save a screenshot to show it is drawn.
    async testContextLoss() {
        const t0 = performance.now()
        const restored = new Promise(r => stage.app.canvas.addEventListener('webglcontextrestored', r, { once: true }))
        stage.app.renderer.context.forceContextLoss()
        await restored
        await contextRebuild
        await nextFrame(); await nextFrame()
        const shot = await writePng(`context-restore-${stage.scene?.name ?? 'placeholder'}-${stampNow()}.png`, await stage.screenshot())
        return `Context lost and restored; scene rebuilt in ${Math.round(performance.now() - t0)} ms\nsaved ${shot.name}`
    },
    screenshotDataUrl: () => stage.screenshot(),
    async saveScreenshot() {
        const shot = await writePng(`screen-${stage.scene?.name ?? 'placeholder'}-${stampNow()}.png`, await stage.screenshot())
        return `saved ${shot.name}`
    },
    autorun: parseAutorun(),
    controller,
    onRunWritten: null,
    onFirstWarFrame,
    attachStage(s) {
        stage = s
        // The atlas and effect canvases are released after GPU upload
        // (scene/atlas.js releaseCanvases), so after a lost WebGL context
        // PixiJS has nothing to re-upload from: rebuild the scene and the
        // beat from scratch once the context is back. PixiJS's own restore
        // listener was added first (app.init), so it has already run.
        s.app.canvas.addEventListener('webglcontextlost', () => debugLog('webgl context lost'))
        s.app.canvas.addEventListener('webglcontextrestored', () => {
            const name = stage.scene?.name
            debugLog('webgl context restored; rebuilding', name ?? 'beat only')
            contextRebuild = name ? app.showFixture(name) : app.rebuildBeat()
            contextRebuild.catch(e => debugLog('rebuild after context restore failed', e))
        })
        app.rebuildBeat()
    },
    ready: () => !!(stage && beat),
    armRun: (cfg, opts) => { stage.resetDrawStats(); resetMemPeak(); controller.arm(cfg, opts) },
    isRecording: () => logger.recording,
    isGraded,
    deviceInfo: () => deviceInfo,
    startupRecord: () => (startupResult ? currentStartupRecord() : null),
    crashSnapshot,
    async exportExtras() {
        const crash = await crashSnapshot()
        return [{ name: `crash-events-${Date.now()}.json`, data: JSON.stringify({ build: BUILD, crash }, null, 1) }]
    },
}

// Web only: let desktop checks drive the app (never present in the native app).
if (!isNative && window.__gate1) window.__gate1.app = app

ReactDOM.createRoot(document.getElementById('root')).render(React.createElement(Gate1App, { app }))
